import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Badge, Button, Card, Group, Loader, SegmentedControl, SimpleGrid, Stack, Table, Text, TextInput, ThemeIcon, rem,
} from '@mantine/core';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import { IconArrowRight, IconChevronRight, IconKey, IconLock, IconSearch, IconShieldCheck, IconUsers } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { fetchRoles, fetchErpRoles, isRoleSelectable, roleLabel } from '@/features/roles/api';
import { fetchList } from '@/shared/api/jsonapi/client';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MasterTenantNotice } from '@/features/tenants/MasterTenantNotice';
import { MobileHeader } from '@/shared/mobile/MobileHeader';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';
import { mw, MW_CARD_SHADOW, MW_CODE_STYLE } from '@/shared/mobile/tokens';
import { StatCard } from '@/shared/components/StatCard';
import { Paginated } from '@/shared/components/DataTable/TablePagination';

export const Route = createFileRoute('/_app/roles/')({ component: RolesListPage });

type Filter = 'all' | 'system' | 'custom' | 'erp';

function RolesListPage() {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const rolesQ = useQuery({ queryKey: ['roles'], queryFn: fetchRoles });
  const roles = useMemo(() => rolesQ.data?.data ?? [], [rolesQ.data]);

  // Vista dedicata "Profili ERP": filtro server-side typology='erp' (13 profili master,
  // read-only per i sottotenant). Le altre viste mantengono il comportamento attuale.
  const erpQ = useQuery({ queryKey: ['roles', 'erp'], enabled: filter === 'erp', queryFn: fetchErpRoles });
  const erpRoles = useMemo(() => erpQ.data?.data ?? [], [erpQ.data]);

  const source = filter === 'erp' ? erpRoles : roles;

  // Conteggi claim + membri per ruolo (page size 1 → meta.total). Reti piccole, in parallelo.
  const statsQ = useQueries({
    queries: source.map((r) => ({
      queryKey: ['role-stats', r.id],
      staleTime: 60_000,
      queryFn: async () => {
        const [claims, members] = await Promise.all([
          fetchList('role-claims', { filter: { 'role.id': { eq: r.id } }, page: { size: 1 } }),
          fetchList('user-roles', { filter: { 'role.id': { eq: r.id } }, page: { size: 1 } }),
        ]);
        return { claims: claims.total, members: members.total };
      },
    })),
  });

  const statsById = useMemo(() => {
    const m = new Map<string, { claims: number; members: number }>();
    source.forEach((r, i) => { const d = statsQ[i]?.data; if (d) m.set(r.id, d); });
    return m;
  }, [source, statsQ]);

  const systemCount = roles.filter((r) => r.isFromMaster).length;
  const customCount = roles.length - systemCount;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return source.filter((r) => {
      if (filter === 'system' && !r.isFromMaster) return false;
      if (filter === 'custom' && r.isFromMaster) return false;
      // La ricerca guarda sia l'etichetta italiana sia la chiave tecnica.
      if (q && !`${roleLabel(r)} ${r.name ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [source, filter, search]);

  const statsLoading = statsQ.some((q) => q.isLoading);

  const navigate = useNavigate();
  const isMobile = useIsMobileShell();
  const listLoading = rolesQ.isLoading || (filter === 'erp' && erpQ.isLoading);

  if (isMobile) {
    const CHIPS: { value: Filter; label: string; count?: number }[] = [
      { value: 'all', label: 'Tutti', count: roles.length },
      { value: 'system', label: 'Sistema', count: systemCount },
      { value: 'custom', label: 'Custom', count: customCount },
      { value: 'erp', label: 'Profili ERP' },
    ];
    return (
      <Stack gap={16} px={20} pt={16} pb={24}>
        <MobileHeader title="Ruoli" />

        <SimpleGrid cols={{ base: 1, xs: 3 }} spacing={10}>
          {([['Totali', roles.length, mw.brand], ['Sistema', systemCount, mw.ink500], ['Custom', customCount, mw.success]] as const).map(([label, value, color]) => (
            <div key={label} style={{ border: `1px solid ${mw.line}`, borderLeft: `3px solid ${color}`, borderRadius: 12, background: mw.surface, boxShadow: MW_CARD_SHADOW, padding: '10px 12px' }}>
              <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: mw.ink300 }}>{label}</Text>
              <Text style={{ fontSize: 'var(--fs-h3)', fontWeight: 700, color: mw.ink900, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
            </div>
          ))}
        </SimpleGrid>

        <TextInput
          placeholder="Cerca ruolo…"
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          radius="md"
        />

        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', margin: '0 -20px', padding: '0 20px 2px' }}>
          {CHIPS.map((c) => {
            const active = c.value === filter;
            return (
              <button
                key={c.value}
                onClick={() => setFilter(c.value)}
                className="mw-pressable"
                style={{
                  flexShrink: 0, height: rem(34), padding: '0 14px', borderRadius: 999, cursor: 'pointer',
                  fontSize: 'var(--fs-compact)', fontWeight: 600, whiteSpace: 'nowrap', fontFamily: 'inherit',
                  border: active ? `1px solid ${mw.brand}` : `1px solid ${mw.line}`,
                  background: active ? mw.brandFill : mw.surface, color: active ? 'var(--on-brand)' : mw.ink500,
                }}
              >
                {c.label}{c.count != null ? <> · <span style={{ fontVariantNumeric: 'tabular-nums' }}>{c.count}</span></> : null}
              </button>
            );
          })}
        </div>

        {listLoading ? (
          <Group p="lg" justify="center"><Loader /></Group>
        ) : visible.length === 0 ? (
          <div style={{ border: `1px solid ${mw.line}`, borderRadius: mw.radiusCard, background: mw.surface, boxShadow: MW_CARD_SHADOW, padding: '28px 18px', textAlign: 'center' }}>
            <IconShieldCheck size={28} color={mw.ink300} />
            <Text style={{ fontSize: 'var(--fs-body-lg)', fontWeight: 700, color: mw.ink900, marginTop: 8 }}>Nessun ruolo</Text>
            <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 2 }}>Nessun ruolo con questi filtri.</Text>
          </div>
        ) : (
          <Stack gap={10}>
            {visible.map((r) => {
              const s = statsById.get(r.id);
              const master = filter === 'erp';
              return (
                <button
                  key={r.id}
                  onClick={() => void navigate({ to: '/roles/$roleId', params: { roleId: r.id } })}
                  className="mw-pressable"
                  style={{ textAlign: 'left', cursor: 'pointer', width: '100%', display: 'flex', alignItems: 'center', gap: 12, border: `1px solid ${mw.line}`, borderRadius: mw.radiusTile, background: mw.surface, boxShadow: MW_CARD_SHADOW, padding: '12px 14px' }}
                >
                  <ThemeIcon variant="light" color={r.isFromMaster || master ? 'gray' : 'teal'} radius="md" size="lg">
                    <IconShieldCheck size={18} />
                  </ThemeIcon>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text style={{ fontSize: 'var(--fs-body)', fontWeight: 600, color: mw.ink900 }} lineClamp={1}>{roleLabel(r)}</Text>
                    {/* Con l'etichetta italiana la chiave tecnica resta visibile sotto: serve per i claim. */}
                    {r.displayName?.trim() && (
                      <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink300, ...MW_CODE_STYLE }} lineClamp={1}>{r.name}</Text>
                    )}
                    <Group gap={6} mt={4} wrap="wrap">
                      {master
                        ? <Badge size="xs" color="indigo" variant="light" leftSection={<IconLock size={10} />}>Master</Badge>
                        : r.isFromMaster
                          ? <Badge size="xs" color="gray" variant="light" leftSection={<IconLock size={10} />}>Sistema</Badge>
                          : <Badge size="xs" color="teal" variant="light">Personalizzato</Badge>}
                      {!isRoleSelectable(r) && (
                        <Badge size="xs" color="gray" variant="outline" title="Profilo di porting senza etichetta: non proposto nelle assegnazioni">Inerte</Badge>
                      )}
                      <Badge size="xs" variant="light" color="blue" leftSection={<IconKey size={10} />}>{s ? s.claims : '—'}</Badge>
                      <Badge size="xs" variant="light" color="grape" leftSection={<IconUsers size={10} />}>{s ? s.members : '—'}</Badge>
                    </Group>
                  </div>
                  <IconChevronRight size={18} style={{ color: mw.ink300, flexShrink: 0 }} />
                </button>
              );
            })}
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconShieldCheck size={22} />}
        title="Ruoli"
        sub="Ruoli, permessi (claim) e membri assegnati."
      />
      <Stack>
      {/* I ruoli del master sono condivisi con tutte le aziende: qui il divieto non è del gesto,
          è della pagina, e va detto prima che qualcuno provi a salvare. */}
      <MasterTenantNotice cosa="I ruoli e i permessi" />
      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard icon={IconShieldCheck} color="brand" label="Ruoli totali" value={roles.length} />
        <StatCard icon={IconLock} color="gray" label="Di sistema" value={systemCount} />
        <StatCard icon={IconKey} color="green" label="Personalizzati" value={customCount} />
      </SimpleGrid>

      <Group justify="space-between" wrap="wrap">
        <SegmentedControl
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          data={[
            { value: 'all', label: 'Tutti' },
            { value: 'system', label: 'Sistema' },
            { value: 'custom', label: 'Personalizzati' },
            { value: 'erp', label: 'Profili ERP' },
          ]}
        />
        <TextInput
          placeholder="Cerca ruolo…"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={240}
        />
      </Group>

      <Card withBorder padding={0} radius="md">
        {rolesQ.isError || erpQ.isError ? (
          <QueryErrorAlert error={rolesQ.error ?? erpQ.error} title="Impossibile leggere i ruoli" onRetry={() => { void rolesQ.refetch(); void erpQ.refetch(); }} />
        ) : rolesQ.isLoading || (filter === 'erp' && erpQ.isLoading) ? (
          <Group p="lg"><Loader /></Group>
        ) : (
          <Paginated rows={visible} label="ruoli">
          {(pageRows) => (
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ruolo</Table.Th>
                <Table.Th>Tipo</Table.Th>
                <Table.Th ta="right">Claim {statsLoading && <Loader size={10} />}</Table.Th>
                <Table.Th ta="right">Membri</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pageRows.map((r) => {
                const s = statsById.get(r.id);
                return (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Group gap="xs">
                        <ThemeIcon variant="light" color={r.isFromMaster ? 'gray' : 'teal'} radius="md" size="sm">
                          <IconShieldCheck size={14} />
                        </ThemeIcon>
                        <Text fw={500}>{roleLabel(r)}</Text>
                        {r.displayName?.trim() && <Text size="xs" c="dimmed" style={MW_CODE_STYLE}>{r.name}</Text>}
                        {!isRoleSelectable(r) && (
                          <Badge size="xs" color="gray" variant="outline" title="Profilo di porting senza etichetta: non proposto nelle assegnazioni">Inerte</Badge>
                        )}
                      </Group>
                      {filter === 'erp' && r.description && (
                        <Text size="xs" c="dimmed" mt={2} pl={34}>{r.description}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {filter === 'erp'
                        ? <Badge color="indigo" variant="light" leftSection={<IconLock size={11} />}>Master</Badge>
                        : r.isFromMaster
                          ? <Badge color="gray" variant="light" leftSection={<IconLock size={11} />}>Sistema</Badge>
                          : <Badge color="teal" variant="light">Personalizzato</Badge>}
                    </Table.Td>
                    <Table.Td ta="right">
                      <Badge variant="light" color="blue">{s ? s.claims : '—'}</Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Badge variant="light" color="grape" leftSection={<IconUsers size={11} />}>{s ? s.members : '—'}</Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Button component={Link} to={`/roles/${r.id}`} size="xs" variant="subtle" rightSection={<IconArrowRight size={14} />}>
                        Apri
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {visible.length === 0 && (
                <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" py="lg">Nessun ruolo con questi filtri.</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          )}
          </Paginated>
        )}
      </Card>
      </Stack>
    </MaeShell>
  );
}
